import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, finalize, of } from 'rxjs';
import { arabicTitleValidator, englishTitleValidator } from '../../../../shared/validators/localized-title.validators';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import type {
  AssetAssignmentDto,
  AssetAssignmentStatus,
  AssetCategoryDto,
  AssetDto,
  AssetMaintenanceDto,
  AssetMaintenanceStatus,
  AssetStatus,
} from '../../../../core/models/assets/assets.dto';
import { AssetsService } from '../../../../core/services/assets.service';
import { ToastFeedbackService } from '../../../../core/services/toast-feedback.service';
import { TableActionsComponent } from '../../../../shared/components/table-actions/table-actions.component';
import { WnAutoFilterDirective } from '../../../../shared/directives/wn-auto-filter.directive';
import { ListPaginatorComponent } from '../../../../shared/components/list-paginator/list-paginator.component';
import { WnRequiredFieldMarkerDirective } from '../../../../shared/directives/wn-required-field-marker.directive';
import { StatusChip } from '../../../../shared/components/status-chip/status-chip';
import { EmptyState } from '../../../../shared/components/empty-state/empty-state';
import { ContentLanguageService, applyBilingualRequired } from '../../../../core/i18n/content-language';
import { BenefitsDialogComponent } from '../benefits/shared/dialogs/benefits-dialog/benefits-dialog.component';
import { AssetLossDialogComponent } from './asset-loss-dialog/asset-loss-dialog.component';
import { titleErrorKey } from './asset-titles';
import type { AssetChargeInput } from '../../../../core/models/assets/asset-charges.dto';

import { WnFocusInvalidOnSubmitDirective } from '../../../../shared/directives/wn-focus-invalid-on-submit.directive';
type AssetsView = 'assets' | 'assignments' | 'maintenance' | 'categories';

@Component({
  selector: 'app-assets-page',
  standalone: true,
  imports: [WnFocusInvalidOnSubmitDirective,
    WnRequiredFieldMarkerDirective,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    WnAutoFilterDirective,
    ListPaginatorComponent,
    TableActionsComponent,
    StatusChip,
    EmptyState,
    BenefitsDialogComponent,
    AssetLossDialogComponent,
  ],
  templateUrl: './assets-page.component.html',
  styleUrl: './assets-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetsPageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(AssetsService);
  private readonly translation = inject(Translation);
  private readonly route = inject(ActivatedRoute);
  private readonly toast = inject(ToastFeedbackService);
  private readonly contentLang = inject(ContentLanguageService);
  /** WN-926: bilingual fields render in tenant-language order (primary first). */
  protected readonly langOrder = this.contentLang.order();

  protected readonly pageSize = signal(20);
  protected readonly assetStatuses: AssetStatus[] = [1, 2, 3, 4, 5, 6];
  protected readonly view = signal<AssetsView>(this.initialViewFromRoute());
  protected readonly isArabic = computed(() => this.translation.getCurrentLang().toLowerCase().startsWith('ar'));
  protected readonly skip = signal(0);
  protected readonly rows = signal<AssetDto[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly categories = signal<AssetCategoryDto[]>([]);
  protected readonly assignments = signal<AssetAssignmentDto[]>([]);
  /** WN-1251: Assignments shows only the pending return requests; `?returnRequested=1` (the notification link) opens it so. */
  protected readonly returnRequestedOnly = signal(this.route.snapshot.queryParamMap?.get('returnRequested') === '1');
  protected readonly maintenances = signal<AssetMaintenanceDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly editingCategoryId = signal<string | null>(null);
  /** WN-986: the category form opens on demand in a dialog, so the list keeps the full width. */
  protected readonly categoryDialogOpen = signal(false);
  protected readonly savingCategory = signal(false);
  protected readonly categoryError = signal<string | null>(null);
  protected readonly titleErrorKey = titleErrorKey;
  /** The asset being marked lost or damaged; the charge dialog is open while this is set. */
  protected readonly lossTarget = signal<{ asset: AssetDto; mode: 'lost' | 'damaged' } | null>(null);

  protected readonly filterForm = this.fb.nonNullable.group({
    search: '',
    categoryId: '',
    status: '',
  });

  protected readonly categoryForm = this.fb.nonNullable.group({
    titleEn: ['', [Validators.required, englishTitleValidator()]],
    titleAr: [''],
    description: '',
    isActive: true,
  });

  constructor() {
    applyBilingualRequired(this.contentLang.primary(), this.categoryForm.controls.titleAr, this.categoryForm.controls.titleEn);
    this.loadCategories();
    this.loadCurrentView();
  }

  protected setView(next: AssetsView): void {
    this.view.set(next);
    this.loadCurrentView();
  }

  protected reloadAssets(): void {
    this.loading.set(true);
    this.error.set(null);
    const value = this.filterForm.getRawValue();
    this.service
      .getAssets({
        skip: this.skip(),
        rowCount: this.pageSize(),
        search: value.search,
        categoryId: value.categoryId,
        status: value.status ? (Number(value.status) as AssetStatus) : '',
      })
      .pipe(
        catchError((err) => {
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
          return of({ items: [], totalCount: 0 });
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((page) => {
        this.rows.set(page.items ?? []);
        this.totalCount.set(page.totalCount ?? 0);
      });
  }

  protected applyFilter(): void {
    this.skip.set(0);
    this.reloadAssets();
  }

  protected resetFilters(): void {
    this.filterForm.reset({
      search: '',
      categoryId: '',
      status: '',
    });
    this.skip.set(0);
    this.reloadAssets();
  }

  protected changePage(nextSkip: number): void {
    const safeSkip = Math.max(0, Math.trunc(nextSkip || 0));
    if (safeSkip === this.skip()) return;
    this.skip.set(safeSkip);
    this.reloadAssets();
  }

  protected changePageSize(nextPageSize: number): void {
    const safePageSize = Math.max(1, Math.trunc(nextPageSize || 0));
    if (safePageSize === this.pageSize()) return;
    this.pageSize.set(safePageSize);
    this.skip.set(0);
    this.reloadAssets();
  }

  protected previousPage(): void {
    if (this.skip() <= 0) return;
    this.skip.set(Math.max(0, this.skip() - this.pageSize()));
    this.reloadAssets();
  }

  protected nextPage(): void {
    if (this.skip() + this.pageSize() >= this.totalCount()) return;
    this.skip.set(this.skip() + this.pageSize());
    this.reloadAssets();
  }

  protected rangeLabel(): string {
    if (!this.totalCount()) return '0';
    const start = this.skip() + 1;
    const end = Math.min(this.skip() + this.pageSize(), this.totalCount());
    return `${start}-${end}`;
  }

  protected openLossDialog(asset: AssetDto, mode: 'lost' | 'damaged'): void {
    this.lossTarget.set({ asset, mode });
  }

  protected closeLossDialog(): void {
    this.lossTarget.set(null);
  }

  protected confirmLoss(charge: AssetChargeInput | null): void {
    const target = this.lossTarget();
    if (!target) return;
    this.lossTarget.set(null);
    this.runAction(target.asset, target.mode, charge ?? undefined);
  }

  protected runAction(asset: AssetDto, action: 'retire' | 'lost' | 'damaged' | 'maintenance', charge?: AssetChargeInput): void {
    const request =
      action === 'retire'
        ? this.service.retire(asset.id)
        : action === 'lost'
          ? this.service.markLost(asset.id, charge)
          : action === 'damaged'
            ? this.service.markDamaged(asset.id, charge)
            : this.service.sendToMaintenance(asset.id);

    this.error.set(null);
    request
      .pipe(
        catchError((err) => {
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
          return of(false);
        }),
      )
      .subscribe((ok) => {
        if (!ok) return;
        this.toast.successKey('ASSETS.ACTION_DONE');
        this.reloadAssets();
      });
  }

  protected saveCategory(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    const raw = this.categoryForm.getRawValue();
    const base = {
      titleAr: raw.titleAr.trim(),
      titleEn: raw.titleEn.trim(),
      description: raw.description.trim() || null,
      isActive: raw.isActive,
    };
    const id = this.editingCategoryId();
    const request = id ? this.service.updateCategory({ id, ...base }) : this.service.createCategory(base);
    this.savingCategory.set(true);
    this.categoryError.set(null);
    request
      .pipe(
        catchError((err) => {
          this.categoryError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
          return of(null);
        }),
        finalize(() => this.savingCategory.set(false)),
      )
      .subscribe((category) => {
        if (!category) return;
        this.toast.successKey('ASSETS.CATEGORY_SAVED');
        this.savingCategory.set(false);
        this.closeCategoryDialog();
        this.loadCategories();
      });
  }

  protected openNewCategory(): void {
    this.resetCategoryForm();
    this.categoryDialogOpen.set(true);
  }

  protected editCategory(category: AssetCategoryDto): void {
    this.categoryError.set(null);
    this.editingCategoryId.set(category.id);
    this.categoryForm.reset({
      titleEn: category.titleEn ?? '',
      titleAr: category.titleAr ?? '',
      description: category.description ?? '',
      isActive: category.isActive,
    });
    this.categoryDialogOpen.set(true);
  }

  protected closeCategoryDialog(): void {
    if (this.savingCategory()) return;
    this.categoryDialogOpen.set(false);
    this.resetCategoryForm();
  }

  protected resetCategoryForm(): void {
    this.editingCategoryId.set(null);
    this.categoryError.set(null);
    this.categoryForm.reset({
      titleEn: '',
      titleAr: '',
      description: '',
      isActive: true,
    });
  }

  protected toggleCategory(category: AssetCategoryDto): void {
    const request = category.isActive ? this.service.deactivateCategory(category.id) : this.service.activateCategory(category.id);
    request
      .pipe(catchError((err) => {
        this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        return of(false);
      }))
      .subscribe((ok) => {
        if (!ok) return;
        this.loadCategories();
      });
  }

  protected title(row: AssetDto | AssetCategoryDto | AssetAssignmentDto | AssetMaintenanceDto): string {
    const anyRow = row as Record<string, unknown>;
    const primary = this.isArabic()
      ? (anyRow['titleAr'] ?? anyRow['assetTitleAr'])
      : (anyRow['titleEn'] ?? anyRow['assetTitleEn']);
    const fallback = this.isArabic()
      ? (anyRow['titleEn'] ?? anyRow['assetTitleEn'])
      : (anyRow['titleAr'] ?? anyRow['assetTitleAr']);
    return String(primary ?? fallback ?? '-');
  }

  protected categoryTitle(row: AssetDto | AssetCategoryDto): string {
    const anyRow = row as Record<string, unknown>;
    const primary = this.isArabic()
      ? (anyRow['assetCategoryTitleAr'] ?? anyRow['titleAr'])
      : (anyRow['assetCategoryTitleEn'] ?? anyRow['titleEn']);
    const fallback = this.isArabic()
      ? (anyRow['assetCategoryTitleEn'] ?? anyRow['titleEn'])
      : (anyRow['assetCategoryTitleAr'] ?? anyRow['titleAr']);
    return String(primary ?? fallback ?? '-');
  }

  protected statusLabel(status: AssetStatus): string {
    return `ASSETS.STATUS.${status}`;
  }

  protected assignmentStatusLabel(status: AssetAssignmentStatus): string {
    return `ASSETS.ASSIGNMENT_STATUS.${status}`;
  }

  protected maintenanceStatusLabel(status: AssetMaintenanceStatus): string {
    return `ASSETS.MAINTENANCE_STATUS.${status}`;
  }

  protected canRetire(asset: AssetDto): boolean {
    return asset.status !== 6;
  }

  protected canSendToMaintenance(asset: AssetDto): boolean {
    return asset.status === 1 || asset.status === 2 || asset.status === 5;
  }

  protected canMarkDamaged(asset: AssetDto): boolean {
    return asset.status !== 4 && asset.status !== 5 && asset.status !== 6;
  }

  protected canMarkLost(asset: AssetDto): boolean {
    return asset.status !== 4 && asset.status !== 6;
  }

  private initialViewFromRoute(): AssetsView {
    const path = this.route.snapshot.routeConfig?.path;
    if (path === 'assignments' || path === 'maintenance' || path === 'categories') return path;
    return 'assets';
  }

  private loadCurrentView(): void {
    const current = this.view();
    if (current === 'assets') this.reloadAssets();
    if (current === 'assignments') this.reloadAssignments();
    if (current === 'maintenance') this.reloadMaintenances();
    if (current === 'categories') this.loadCategories();
  }

  private loadCategories(): void {
    this.service
      .getCategories()
      .pipe(catchError(() => of({ items: [], totalCount: 0 })))
      .subscribe((page) => this.categories.set(page.items ?? []));
  }

  protected setReturnRequestedOnly(next: boolean): void {
    if (next === this.returnRequestedOnly()) return;
    this.returnRequestedOnly.set(next);
    this.reloadAssignments();
  }

  /** Asked back and still out: the admin has a return to process. */
  protected isReturnPending(row: AssetAssignmentDto): boolean {
    return !!row.returnRequestedAt && !row.returnedAt && (row.assignmentStatus === 1 || row.assignmentStatus === 3);
  }

  private reloadAssignments(): void {
    this.loading.set(true);
    this.service
      .getAssignments({ returnRequestedOnly: this.returnRequestedOnly() })
      .pipe(
        catchError(() => of({ items: [], totalCount: 0 })),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((page) => this.assignments.set(page.items ?? []));
  }

  private reloadMaintenances(): void {
    this.loading.set(true);
    this.service
      .getMaintenances()
      .pipe(
        catchError(() => of({ items: [], totalCount: 0 })),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((page) => this.maintenances.set(page.items ?? []));
  }
}
